import { noop } from '@cian/newbuilding-utils';

import { type DependencyList, type RefObject, useEffect, useMemo, useRef, useState } from 'react';

type TSubscribeState = {
  setSubscribeAvailable(): void;
  setSubscribeUnavailable(): void;
  subscribeAvailable: boolean;
};

type IntersectionsCount = { inView: number; outView: number };

type TLifecycleHookEvent = {
  observer: IntersectionObserver;
  subscribeState: TSubscribeState;
  entries: IntersectionObserverEntry[];
  intersectionsCount: IntersectionsCount;
};

type TUseIntersectionObserverParams<Element extends HTMLElement = HTMLElement> = {
  elementRef: RefObject<Element | null>;
  deps?: DependencyList;
  initSubscribeAvailable?: boolean;
  observerOptions?: IntersectionObserverInit;
  onChange?(params: TLifecycleHookEvent): void;
  onInViewport?(params: TLifecycleHookEvent): void;
  onOutViewport?(params: TLifecycleHookEvent): void;
};

type TUseIntersectionObserverResult = {
  subscribeState: TSubscribeState;
};

export function useIntersectionObserver<Element extends HTMLElement = HTMLElement>(
  params: TUseIntersectionObserverParams<Element>,
): TUseIntersectionObserverResult {
  const {
    deps = [],
    elementRef,
    initSubscribeAvailable = true,
    observerOptions,
    onChange = noop,
    onInViewport = noop,
    onOutViewport = noop,
  } = params;

  const subscribeAvailableRef = useRef<boolean>(initSubscribeAvailable);
  const intersectionsCount = useRef<IntersectionsCount>({ inView: 0, outView: 0 });
  const [isSubscribeAvailable, setIsSubscribeAvailable] = useState(initSubscribeAvailable);

  const subscribeState = useMemo<TSubscribeState>(
    () => ({
      setSubscribeAvailable(): void {
        subscribeAvailableRef.current = true;
        setIsSubscribeAvailable(true);
      },
      setSubscribeUnavailable(): void {
        subscribeAvailableRef.current = false;
        setIsSubscribeAvailable(false);
      },
      get subscribeAvailable(): boolean {
        return subscribeAvailableRef.current;
      },
    }),
    [],
  );

  useEffect((): void | VoidFunction => {
    if (isSubscribeAvailable && elementRef.current) {
      const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          intersectionsCount.current.inView += 1;

          onInViewport({
            entries,
            intersectionsCount: intersectionsCount.current,
            observer,
            subscribeState,
          });
        } else {
          intersectionsCount.current.outView += 1;

          onOutViewport({
            entries,
            intersectionsCount: intersectionsCount.current,
            observer,
            subscribeState,
          });
        }

        onChange({
          entries,
          intersectionsCount: intersectionsCount.current,
          observer,
          subscribeState,
        });
      }, observerOptions);

      observer.observe(elementRef.current);

      return () => {
        observer.disconnect();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps.concat(isSubscribeAvailable));

  return { subscribeState };
}
